"use client";

import {
  Archive,
  Box,
  Building,
  Clipboard,
  DollarSign,
  FastForwardIcon,
  Home,
  Tags,
  Truck,
  Users,
  XOctagon,
  Percent,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavUser } from "./NavUser";
import { useLocation, useNavigate } from "react-router-dom";
import { DashboardIcon } from "@radix-ui/react-icons";
import { OrganizationInfo } from "./SideBarOrganizationInfo";
import type { OrganizationsOutput } from "@/interfaces";
import { usePermissions } from "@/hooks/use-permissions";
import { useAppManager } from "@/context/AppManagerContext";
import { useEffect } from "react";
import { cn } from "@/lib/utils";

interface AppSidebarProps {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  organization: OrganizationsOutput;
}

export function AppSidebar({ user, organization }: Readonly<AppSidebarProps>) {
  const { currentRole, currentPlan, currentOrganization } = useAppManager();
  const { open, setOpen, isMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();

  // Fechar a sidebar no mobile quando a rota mudar
  useEffect(() => {
    if (isMobile && open) {
      setOpen(false);
    }
  }, [location.pathname, isMobile, open, setOpen]);

  const system = [
    { title: "Início", url: "/inicio", icon: Home },
    {
      title: "Minha organização",
      url: `/organizacoes/${currentOrganization!.id}`,
      icon: Building,
    },
  ];

  const registrations = [
    { title: "Pedidos", url: "/pedidos", icon: Clipboard },
    {
      title: "Pedidos Rápidos",
      url: "/pedidos-rapidos",
      icon: FastForwardIcon,
    },
    { title: "Clientes", url: "/clientes", icon: Users },
    { title: "Produtos", url: "/produtos", icon: Box },
    { title: "Ofertas", url: "/ofertas", icon: Percent },
    { title: "Tags", url: "/tags", icon: Tags },
    { title: "Despesas", url: "/despesas", icon: DollarSign },
    { title: "Catálogos", url: "/catalogos", icon: Archive },
  ];

  const reports = [
    { title: "Dashboard", url: "/dashboard", icon: DashboardIcon },
    { title: "Inadimplentes", url: "/inadimplentes", icon: XOctagon },
    { title: "Calendário de entregas", url: "/entregas", icon: Truck },
  ];

  const {
    canAccessDashboard,
    canAccessDelinquency,
    canAccessCalendar,
    canAccessMenu,
  } = usePermissions({
    userRole: currentRole,
    planFeatures: currentPlan?.features ?? [],
  });

  const filteredReports = reports.filter((item) => {
    if (item.url === "/dashboard") return canAccessDashboard;
    if (item.url === "/inadimplentes") return canAccessDelinquency;
    if (item.url === "/entregas") return canAccessCalendar;

    return true;
  });

  const filteredRegistrations = registrations.filter((item) => {
    if (item.url === "/catalogos") return canAccessMenu;
    return true;
  });

  // Verificar se um item está ativo
  const isActive = (url: string) => {
    return location.pathname === url || location.pathname.startsWith(`${url}/`);
  };

  return (
    <>
      {/* Overlay para fechar a sidebar no mobile */}
      {isMobile && open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={() => setOpen(false)}
        />
      )}

      <div className={cn("dark", isMobile && "fixed inset-y-0 left-0 z-50")}>
        <Sidebar
          className={cn(
            "transition-all duration-300 ease-in-out border-r border-gray-800",
            isMobile && "w-[280px] shadow-xl"
          )}
        >
          <SidebarHeader>
            <OrganizationInfo organization={organization} />
          </SidebarHeader>
          <SidebarContent className="px-2">
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-semibold text-gray-400 px-3 py-2">
                Sistema
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {system.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        onClick={() => navigate(item.url)}
                        className={cn(
                          "transition-colors rounded-lg mb-1",
                          isActive(item.url) &&
                            "bg-[#EE7B11]/20 text-[#EE7B11] font-medium"
                        )}
                      >
                        <div className="flex items-center gap-3 px-3 py-2">
                          <item.icon
                            className={cn(
                              "h-5 w-5",
                              isActive(item.url) && "text-[#EE7B11]"
                            )}
                          />
                          <span>{item.title}</span>
                          {isActive(item.url) && (
                            <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-[#EE7B11]" />
                          )}
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            {filteredReports.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel className="text-xs font-semibold text-gray-400 px-3 py-2">
                  Relatórios
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {filteredReports.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          onClick={() => navigate(item.url)}
                          className={cn(
                            "transition-colors rounded-lg mb-1 ",
                            isActive(item.url) &&
                              "bg-[#EE7B11]/20 text-[#EE7B11] font-medium"
                          )}
                        >
                          <div className="flex items-center gap-3 px-3 py-2">
                            <item.icon
                              className={cn(
                                "h-5 w-5",
                                isActive(item.url) && "text-[#EE7B11]"
                              )}
                            />
                            <span>{item.title}</span>
                            {isActive(item.url) && (
                              <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-[#EE7B11]" />
                            )}
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-semibold text-gray-400 px-3 py-2">
                Cadastros
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredRegistrations.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        onClick={() => navigate(item.url)}
                        className={cn(
                          "transition-colors rounded-lg mb-1",
                          isActive(item.url) &&
                            "bg-[#EE7B11]/20 text-[#EE7B11] font-medium"
                        )}
                      >
                        <div className="flex items-center gap-3 px-3 py-2">
                          <item.icon
                            className={cn(
                              "h-5 w-5",
                              isActive(item.url) && "text-[#EE7B11]"
                            )}
                          />
                          <span>{item.title}</span>
                          {isActive(item.url) && (
                            <div className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-[#EE7B11]" />
                          )}
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter className="border-t border-gray-800 pt-2">
            <NavUser user={user} />
          </SidebarFooter>
        </Sidebar>
      </div>
    </>
  );
}
